/**
 * Русское имя исхода против английских `outcomes` Polymarket.
 *
 * Лента приходит по-русски («Хачанов, Карен»), биржа отвечает по-английски
 * («Karen Khachanov»), и сравнение «как есть» (`normName`) не сводит их никогда.
 * Отсюда 09.10 весь журнал охоты состоял из одной строки — «токен исхода с
 * биржи не открылся»: ссылка была, рынок находился, а сторону выбрать было
 * нечем. Поиск Gamma по русскому имени тоже пуст: «Хачанов» — 0 событий,
 * «Khachanov» — тот самый матч.
 *
 * Сводим в три шага: транслитерация, снятие шума написания, сравнение слов с
 * допуском в одну букву. Шум — это ровно те расхождения, которые видно на
 * живых парах: «Пэлас» против «Palace», «Мингечаур» против «Mingachevir»,
 * «Касима» против «Kashiwa», «Пеньяроль» против «Penarol».
 *
 * Чего здесь нет намеренно — догадок. Сторона называется только при РОВНО
 * одном попадании: свелось с обоими исходами или ни с одним — молчим, и охота
 * пишет, что токен не открылся. Купить не тот исход дороже, чем не купить.
 */

/** Как кириллица выглядит в английских именах площадок, не ГОСТ. */
const LETTERS: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'i',
  ь: '', э: 'e', ю: 'iu', я: 'ia',
};

export function latin(raw: string): string {
  return [...raw.toLowerCase()].map((one) => LETTERS[one] ?? one).join('');
}

/**
 * Снять шум написания одного и того же имени: диакритику, `c`/`k`, `y`/`i`,
 * `ph`/`f`, немую `h`, сдвоенные буквы. После этого «Crystal Palace» и
 * «Кристал Пэлас» — одна строка слов.
 */
export function fold(raw: string): string {
  return latin(raw)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/ph/g, 'f')
    .replace(/c/g, 'k')
    .replace(/y/g, 'i')
    .replace(/h/g, '')
    .replace(/(.)\1+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Слова имени. Короткие отбрасываем: «ФК», «de», инициалы ничего не решают. */
export function words(raw: string): string[] {
  return fold(raw)
    .split(' ')
    .filter((one) => one.length >= 3);
}

/**
 * Одно слово с допуском в одну букву: «feri» ↔ «fery».
 *
 * Трёхбуквенным допуска нет: на такой длине одна правка превращает слово в
 * любое другое, и «фер» сошлось бы с «фери» соперника.
 */
export function near(left: string, right: string): boolean {
  if (left === right) return true;
  const [short, long] = left.length <= right.length ? [left, right] : [right, left];
  if (short.length < 4) return false;
  if (long.startsWith(short)) return true;
  if (long.length - short.length > 1) return false;
  let i = 0;
  let j = 0;
  let slips = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      i += 1;
      j += 1;
      continue;
    }
    slips += 1;
    if (slips > 1) return false;
    if (left.length === right.length) {
      i += 1;
      j += 1;
    } else if (left.length > right.length) i += 1;
    else j += 1;
  }
  return slips + (left.length - i) + (right.length - j) <= 1;
}

/** Одно и то же имя: хоть одно слово сошлось. «Yes» / «No» не сойдутся ни с чем. */
export function sameName(left: string, right: string): boolean {
  const mine = words(left);
  const theirs = words(right);
  if (mine.length === 0 || theirs.length === 0) return false;
  return mine.some((one) => theirs.some((two) => near(one, two)));
}
